import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Stack,
  CircularProgress,
  Paper,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Refresh as ReplanIcon,
  NightsStay as CloseIcon,
  WbSunny as TomorrowIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Article as BriefingIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  rhythmApi,
  billingApi,
  decisionsApi,
  dailyPlanApi,
  workItemsApi,
} from '../api/services';
import { useAuth } from '../auth/AuthProvider';
import type { WorkItem } from '../types/models';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BoltIcon from '@mui/icons-material/Bolt';
import type {
  StartMyDayResponse,
  Decision,
  CloseDayResponse,
} from '../types/models';
import {
  deriveCapacity,
  deriveNextAction,
  derivePriorities,
  deriveRisks,
} from '../utils/dashboard';
import NextActionCard from '../components/dashboard/NextActionCard';
import PriorityList from '../components/dashboard/PriorityList';
import RiskStrip from '../components/dashboard/RiskStrip';
import CapacityStrip from '../components/dashboard/CapacityStrip';
import WeeklyFocusKpi from '../components/dashboard/WeeklyFocusKpi';
import DailyBriefingDialog from '../components/briefing/DailyBriefingDialog';
import WhyAffordance from '../components/ai/WhyAffordance';
import CoachingInsightsCard from '../components/CoachingInsightsCard';
import { buildRationaleMap } from '../utils/aiRationale';
import { Badge } from '@mui/material';

// ---------------------------------------------------------------------------
// DashboardBriefingPage — the new Phase 1 dashboard. Leads with the four
// signal-led surfaces:
//   1. NextActionCard   (hero, single primary action)
//   2. PriorityList     (three-at-most, driver-tagged)
//   3. RiskStrip        (only when something is at risk or breached)
//   4. CapacityStrip    (one-line committed / protected / free)
//
// Everything denser lives below the fold. Close-day, overflow auto-schedule,
// decision resolution, and the full work-items table are still reachable, but
// they are no longer the first thing the user sees.
// ---------------------------------------------------------------------------

export default function DashboardBriefingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [dayData, setDayData] = useState<StartMyDayResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [closingDay, setClosingDay] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [closeResult, setCloseResult] = useState<CloseDayResponse | null>(null);
  const [pendingDecisions, setPendingDecisions] = useState<Decision[]>([]);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState<number | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [briefingDialogOpen, setBriefingDialogOpen] = useState(false);

  const today = format(new Date(), 'yyyy-MM-dd');
  const prettyDate = format(new Date(), 'EEEE d MMMM');

  // Role gates mirror UnifiedPlanPage + usePlanState semantics.
  // Only the executive (or an Admin acting in their seat) can approve or
  // activate. EAs drive the rhythm buttons and replan but cannot sign off.
  const isEa = user?.role === 'ExecutiveAssistant';
  const planStatus = dayData?.plan?.status;
  const canApprove = !isEa && planStatus === 'Proposed';
  const canActivate = !isEa && planStatus === 'Approved';

  // ---- Data loaders -------------------------------------------------------

  const loadDay = useCallback(async (action: 'start' | 'replan' = 'start') => {
    setError(null);
    setLoading(true);
    try {
      const res = action === 'start' ? await rhythmApi.startMyDay() : await rhythmApi.replan();
      setDayData(res);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Unable to load today\u2019s plan.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCloseDay = useCallback(async () => {
    setClosingDay(true);
    try {
      // Close-day reflection is captured by the classic dashboard's guided
      // flow. For the briefing preview we just close without reflection
      // content; the user can fill it in on /reflections after the fact.
      const res = await rhythmApi.closeDay({});
      setCloseResult(res);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Unable to close the day.');
    } finally {
      setClosingDay(false);
    }
  }, []);

  const handlePlanTomorrow = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await rhythmApi.planTomorrow();
      setDayData(res);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Unable to plan tomorrow.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Plan-status transitions. Proposed -> Approved -> Active. Mirror the
  // `usePlanState` / UnifiedPlanPage behaviour so the briefing dashboard is
  // now self-sufficient -- users no longer need to discover /plan to
  // progress past Proposed.
  const handleApprove = useCallback(async () => {
    setError(null);
    setTransitioning(true);
    try {
      const updated = await dailyPlanApi.approve(today);
      setDayData((prev) => (prev ? { ...prev, plan: updated } : prev));
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Unable to approve the plan.');
    } finally {
      setTransitioning(false);
    }
  }, [today]);

  const handleActivate = useCallback(async () => {
    setError(null);
    setTransitioning(true);
    try {
      const updated = await dailyPlanApi.activate(today);
      setDayData((prev) => (prev ? { ...prev, plan: updated } : prev));
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Unable to activate the plan.');
    } finally {
      setTransitioning(false);
    }
  }, [today]);

  // Mark a priority complete from the briefing dashboard itself so the user
  // does not have to round-trip to /work-items or press Replan to see the
  // dashboard refresh. Matches the classic dashboard's optimistic pattern.
  const handleCompleteWorkItem = useCallback(async (id: string) => {
    try {
      await workItemsApi.update(id, { status: 'Done' as WorkItem['status'] });
      setDayData((prev) => {
        if (!prev) return prev;
        const markDone = (items: WorkItem[]) =>
          items.map((i) => (i.id === id ? { ...i, status: 'Done' as WorkItem['status'] } : i));
        return {
          ...prev,
          overflowItems: markDone(prev.overflowItems),
          carryForwardItems: markDone(prev.carryForwardItems),
          carriedForwardFromYesterday: markDone(prev.carriedForwardFromYesterday),
          scheduledBlocks: prev.scheduledBlocks.map((b) =>
            b.workItem?.id === id
              ? { ...b, workItem: { ...b.workItem, status: 'Done' as WorkItem['status'] } }
              : b,
          ),
        };
      });
    } catch {
      // Best effort. If it fails, the row stays in its current state and the
      // user can retry; no destructive local change has been made.
    }
  }, []);

  // ---- Mount ------------------------------------------------------------

  useEffect(() => {
    decisionsApi.getPending().then(setPendingDecisions).catch(() => { /* best effort */ });
    billingApi.getStatus()
      .then((s) => {
        if (s?.trialDaysRemaining !== undefined && s?.trialDaysRemaining !== null) {
          setTrialDaysRemaining(s.trialDaysRemaining);
        }
      })
      .catch(() => { /* best effort */ });
  }, []);

  // ---- Derivations -------------------------------------------------------
  // Memoised so the four sub-components do not re-derive on every render.

  const nextAction = useMemo(() => deriveNextAction(dayData), [dayData]);
  const priorities = useMemo(() => derivePriorities(dayData, 3), [dayData]);
  const risks = useMemo(
    () => deriveRisks(dayData, { pendingDecisionCount: pendingDecisions.length }),
    [dayData, pendingDecisions.length],
  );
  const capacityLine = useMemo(
    () => (dayData ? deriveCapacity(dayData.capacity) : null),
    [dayData],
  );
  const rationaleMap = useMemo(() => buildRationaleMap(dayData), [dayData]);
  const nextActionRationale = nextAction?.workItemId
    ? rationaleMap.get(nextAction.workItemId)
    : undefined;

  // ---- Render -----------------------------------------------------------

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Header: date + plan-lifecycle actions. Kept light so the eye lands
          on the next-action card below, not on the button row. */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, letterSpacing: '-0.01em' }}
            >
              {prettyDate}
            </Typography>
            <WhyAffordance
              rationale={dayData?.plan?.aiRationale}
              secondary={dayData?.aiStrategyAlignment}
              label="Why this shape of day"
            />
          </Stack>
          {dayData?.plan?.dailyTheme && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
              {dayData.plan.dailyTheme}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {dayData?.plan && (
            <Badge
              color="primary"
              variant="dot"
              invisible={
                !dayData.plan.executiveBriefing?.trim() &&
                !dayData.plan.handoffNotes?.trim()
              }
              overlap="circular"
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<BriefingIcon />}
                onClick={() => setBriefingDialogOpen(true)}
              >
                Daily briefing
              </Button>
            </Badge>
          )}
          {!dayData ? (
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <StartIcon />}
              onClick={() => loadDay('start')}
              disabled={loading}
            >
              Start my day
            </Button>
          ) : (
            <>
              {/* Plan-progression buttons take priority visually when the plan
                  is waiting on the executive. Approve moves Proposed ->
                  Approved; Activate moves Approved -> Active. Both are gated
                  by role so an EA never sees them. */}
              {canApprove && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleApprove}
                  disabled={transitioning || loading}
                >
                  Approve today&rsquo;s plan
                </Button>
              )}
              {canActivate && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<BoltIcon />}
                  onClick={handleActivate}
                  disabled={transitioning || loading}
                >
                  Activate plan
                </Button>
              )}
              <Tooltip title="Sync the calendar and re-allocate focus time around today's meetings. Use this whenever a new meeting has landed since the plan was generated.">
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ReplanIcon />}
                    onClick={() => loadDay('replan')}
                    disabled={loading}
                  >
                    Replan
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloseIcon />}
                onClick={handleCloseDay}
                disabled={closingDay}
              >
                Close day
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<TomorrowIcon />}
                onClick={handlePlanTomorrow}
                disabled={loading}
              >
                Plan tomorrow
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      <DailyBriefingDialog
        open={briefingDialogOpen}
        onClose={() => setBriefingDialogOpen(false)}
        date={today}
        plan={dayData?.plan ?? null}
        onUpdated={(updated) => setDayData((d) => (d ? { ...d, plan: updated } : d))}
      />

      {/* Trial banner — unchanged pattern from the classic dashboard. */}
      {trialDaysRemaining !== null && trialDaysRemaining >= 0 && trialDaysRemaining <= 14 && (
        <Alert severity={trialDaysRemaining <= 3 ? 'warning' : 'info'} sx={{ mb: 3 }}>
          Free trial: {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} remaining.
          {' '}
          <Button size="small" onClick={() => navigate('/billing')}>
            Choose a plan
          </Button>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {closeResult && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setCloseResult(null)}>
          Day closed. {closeResult.itemsToCarryForward} item{closeResult.itemsToCarryForward === 1 ? '' : 's'} carried forward.
        </Alert>
      )}

      {/* Coaching insights live here so the briefing dashboard surfaces any
          stalled drivers / red projects / overdue items before the plan
          reads as "everything looks fine". Self-hides when there's nothing. */}
      <CoachingInsightsCard />

      {/* Primary composition. Above the fold on a typical 1080p viewport. */}
      {loading && !dayData ? (
        <Stack spacing={2}>
          <NextActionCard action={null} loading />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Box sx={{ flex: 2, minWidth: 0 }}>
              <PriorityList items={[]} loading />
            </Box>
            <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
              <RiskStrip risks={[]} loading />
              <CapacityStrip capacity={null} loading />
            </Stack>
          </Stack>
        </Stack>
      ) : dayData ? (
        <Stack spacing={2}>
          <NextActionCard
            action={nextAction}
            rationale={nextActionRationale?.rationale}
            rationaleStrategyLink={nextActionRationale?.strategyLink}
            onPlanTomorrow={handlePlanTomorrow}
          />

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
            <Box sx={{ flex: 2, minWidth: 0 }}>
              <PriorityList
                items={priorities}
                rationaleMap={rationaleMap}
                onComplete={handleCompleteWorkItem}
              />
            </Box>
            <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
              <RiskStrip risks={risks} aiRationale={dayData?.aiRisks} />
              {capacityLine && <CapacityStrip capacity={capacityLine} />}
              <WeeklyFocusKpi />
            </Stack>
          </Stack>
        </Stack>
      ) : (
        // Pre-start empty state. Teaching copy tells the user what this is,
        // why it is empty, and how to start — per §6 empty-state rules.
        <Paper
          sx={{
            p: 5,
            textAlign: 'center',
            borderRadius: 3,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            No plan for today yet
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, maxWidth: 520, mx: 'auto' }}>
            Start my day pulls your calendar, unfinished work, and strategic drivers into one ordered plan.
            It protects focus time, flags risk, and gives you the single next action to take.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<StartIcon />}
            onClick={() => loadDay('start')}
            disabled={loading}
          >
            Start my day
          </Button>
        </Paper>
      )}

      {/* Below-the-fold: secondary detail. Collapsed by default; expanded
          state persists in session for the duration of the visit. */}
      {dayData && (
        <Box sx={{ mt: 4 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ cursor: 'pointer', userSelect: 'none', py: 1.5 }}
            onClick={() => setDetailsOpen((v) => !v)}
          >
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.12em', fontWeight: 600 }}>
              Today in detail
            </Typography>
            <IconButton size="small" aria-label={detailsOpen ? 'Hide detail' : 'Show detail'}>
              {detailsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>
          <Collapse in={detailsOpen} timeout={180} unmountOnExit>
            <DetailPanel
              data={dayData}
              pendingDecisionCount={pendingDecisions.length}
              onNavigateWorkItems={() => navigate('/work-items')}
              onNavigateCalendar={() => navigate('/calendar')}
              onNavigateStrategy={() => navigate('/strategy')}
            />
          </Collapse>
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Detail panel — everything the classic dashboard showed, rendered with
// lighter visual weight and links out to dedicated pages. Extracted so it
// only mounts when the user expands the section.
// ---------------------------------------------------------------------------

interface DetailPanelProps {
  data: StartMyDayResponse;
  pendingDecisionCount: number;
  onNavigateWorkItems: () => void;
  onNavigateCalendar: () => void;
  onNavigateStrategy: () => void;
}

function DetailPanel({
  data,
  pendingDecisionCount,
  onNavigateWorkItems,
  onNavigateCalendar,
  onNavigateStrategy,
}: DetailPanelProps) {
  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      {/* AI briefing: rendered as prose, since the model output is narrative. */}
      {data.aiBriefing && (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            AI briefing
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
            {data.aiBriefing}
          </Typography>
        </Paper>
      )}

      {/* Counts + jump links. Each cell is a terse summary that points to
          the dedicated page for depth — keeps this panel from turning into a
          mini-dashboard of its own. */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} divider={<Box sx={{ borderRight: 1, borderColor: 'divider' }} />}>
          <DetailStat
            label="Scheduled"
            value={data.scheduledBlocks.length}
            suffix="blocks"
            onClick={onNavigateCalendar}
          />
          <DetailStat
            label="Overflow"
            value={data.overflowItems.length}
            suffix={data.overflowItems.length === 1 ? 'item' : 'items'}
            onClick={onNavigateWorkItems}
          />
          <DetailStat
            label="Carry-forward"
            value={data.carryForwardItems.length}
            suffix={data.carryForwardItems.length === 1 ? 'item' : 'items'}
            onClick={onNavigateWorkItems}
          />
          <DetailStat
            label="Decisions pending"
            value={pendingDecisionCount}
            suffix=""
            onClick={onNavigateWorkItems}
          />
          <DetailStat
            label="Strategic drivers"
            value={data.strategicDrivers.length}
            suffix={data.strategicDrivers.length === 1 ? 'active' : 'active'}
            onClick={onNavigateStrategy}
          />
        </Stack>
      </Paper>

      {data.aiStrategyAlignment && (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Strategic alignment
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
            {data.aiStrategyAlignment}
          </Typography>
        </Paper>
      )}

      {data.plan?.status && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle2">Plan status</Typography>
          <Chip size="small" label={data.plan.status} />
          {data.plan.preparedBy && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Prepared by {data.plan.preparedBy}
            </Typography>
          )}
        </Paper>
      )}
    </Stack>
  );
}

function DetailStat({
  label,
  value,
  suffix,
  onClick,
}: {
  label: string;
  value: number;
  suffix: string;
  onClick?: () => void;
}) {
  return (
    <Box
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      sx={{
        flex: 1,
        minWidth: 0,
        cursor: onClick ? 'pointer' : 'default',
        '&:hover .detail-stat-value': onClick ? { color: 'primary.main' } : undefined,
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 0.25 }}>
        <Typography
          className="detail-stat-value"
          variant="h6"
          sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', transition: 'color 120ms' }}
        >
          {value}
        </Typography>
        {suffix && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {suffix}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
